import { useMemo } from "react"
import { roleApi } from "@/api/client/role"
import { usePermission } from "@/features/auth/hooks/usePermission"

export function useAdminRoles() {
  const { canAccess } = usePermission()

  const { data } = roleApi.useRoleList({
    query: {
      enabled: canAccess("roles"),
      staleTime: 5 * 60 * 1000,
      retry: false,
    },
  })

  return useMemo(() => {
    const roles = data?.roles ?? []

    const uniqueRoleNames = [...new Set(roles.map((role) => role.role))]

    const scopedKey = (role: string, organizationName: string | null | undefined) =>
      `${role}:${organizationName ?? ""}`

    const labelByScopedKey = new Map(
      roles.map((role) => [scopedKey(role.role, role.organizationName), role.label])
    )
    const labelByRole = new Map<string, string>()
    for (const role of roles) {
      if (!labelByRole.has(role.role)) {
        labelByRole.set(role.role, role.label)
      }
    }

    const getLabel = (role: string | undefined | null, organizationName?: string | null) => {
      if (!role) return ""
      return (
        labelByScopedKey.get(scopedKey(role, organizationName)) ?? labelByRole.get(role) ?? role
      )
    }

    return {
      roles,
      options: uniqueRoleNames.map((role) => ({ value: role, label: getLabel(role) })),
      getOptionsForOrganization: (organizationId: string) =>
        roles
          .filter((role) => role.organizationId === null || role.organizationId === organizationId)
          .map((role) => ({ value: role.role, label: role.label })),
      getLabel,
    }
  }, [data])
}
